import { useState, useCallback } from 'react';
import { 
  ErrorInfo, 
  handleError, 
  showErrorMessage, 
  showSuccessMessage, 
  showWarningMessage,
  showInfoMessage,
  getUserFriendlyErrorMessage,
  logError,
  retryOperation
} from '../utils/errorHandler';
import { Toast } from '../components/common/Toast';

/**
 * Error handling state interface
 */
export interface ErrorHandlingState {
  error: ErrorInfo | null;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Error handling hook options
 */
export interface UseErrorHandlingOptions {
  showToast?: boolean;
  logErrors?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

/**
 * Error handling hook return type
 */
export interface UseErrorHandlingReturn {
  // State
  error: ErrorInfo | null;
  isLoading: boolean;
  hasError: boolean;
  
  // Actions
  setError: (error: any) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  
  // Utilities
  handleAsyncOperation: <T>(
    operation: () => Promise<T>,
    options?: {
      onSuccess?: (result: T) => void;
      onError?: (error: ErrorInfo) => void;
      successMessage?: string;
      showErrorToast?: boolean;
    }
  ) => Promise<T | null>;
  
  retryLastOperation: () => Promise<void>;
  
  // Toast utilities
  showSuccess: (message: string) => void;
  showError: (error: ErrorInfo | any) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

/**
 * Custom hook for comprehensive error handling
 */
export function useErrorHandling(options: UseErrorHandlingOptions = {}): UseErrorHandlingReturn {
  const {
    showToast = true,
    logErrors = true,
    retryAttempts = 3,
    retryDelay = 1000,
  } = options;

  // State
  const [state, setState] = useState<ErrorHandlingState>({
    error: null,
    isLoading: false,
    hasError: false,
  });

  // Store last operation for retry functionality
  const [lastOperation, setLastOperation] = useState<(() => Promise<any>) | null>(null);

  // Set error
  const setError = useCallback((error: any) => {
    const errorInfo = error?.type ? error as ErrorInfo : handleError(error);
    
    setState(prev => ({
      ...prev,
      error: errorInfo,
      hasError: true,
      isLoading: false,
    }));

    // Log error if enabled
    if (logErrors) {
      logError(errorInfo, { timestamp: new Date().toISOString() });
    }

    // Show toast if enabled
    if (showToast) {
      Toast.showError(errorInfo);
    }
  }, [showToast, logErrors]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
      hasError: false,
    }));
  }, []);

  // Set loading state
  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading: loading,
    }));
  }, []);

  // Handle async operations with error handling
  const handleAsyncOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    operationOptions: {
      onSuccess?: (result: T) => void;
      onError?: (error: ErrorInfo) => void;
      successMessage?: string;
      showErrorToast?: boolean;
    } = {}
  ): Promise<T | null> => {
    const {
      onSuccess,
      onError,
      successMessage,
      showErrorToast = showToast,
    } = operationOptions;

    try {
      // Clear previous error and set loading
      clearError();
      setLoading(true);
      
      // Store operation for retry
      setLastOperation(() => operation);

      // Execute operation with retry logic
      const result = await retryOperation(operation, retryAttempts, retryDelay);
      
      // Handle success
      setLoading(false);
      
      if (successMessage) {
        showSuccessMessage(successMessage);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      // Handle error
      const errorInfo = handleError(error);
      
      setState(prev => ({
        ...prev,
        error: errorInfo,
        hasError: true,
        isLoading: false,
      }));

      // Log error
      if (logErrors) {
        logError(errorInfo, { 
          operation: operation.name || 'anonymous',
          timestamp: new Date().toISOString() 
        });
      }

      // Show error toast
      if (showErrorToast) {
        Toast.showError(errorInfo);
      }

      // Call error callback
      if (onError) {
        onError(errorInfo);
      }

      return null;
    }
  }, [showToast, logErrors, retryAttempts, retryDelay, clearError, setLoading]);

  // Retry last operation
  const retryLastOperation = useCallback(async () => {
    if (lastOperation) {
      await handleAsyncOperation(lastOperation);
    }
  }, [lastOperation, handleAsyncOperation]);

  // Toast utilities
  const showSuccess = useCallback((message: string) => {
    showSuccessMessage(message);
  }, []);

  const showError = useCallback((error: ErrorInfo | any) => {
    const errorInfo = error?.type ? error as ErrorInfo : handleError(error);
    showErrorMessage(errorInfo);
  }, []);

  const showWarning = useCallback((message: string) => {
    showWarningMessage(message);
  }, []);

  const showInfo = useCallback((message: string) => {
    showInfoMessage(message);
  }, []);

  return {
    // State
    error: state.error,
    isLoading: state.isLoading,
    hasError: state.hasError,
    
    // Actions
    setError,
    clearError,
    setLoading,
    
    // Utilities
    handleAsyncOperation,
    retryLastOperation,
    
    // Toast utilities
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
}

/**
 * Container-specific error handling hook
 */
export function useContainerErrorHandling() {
  const errorHandling = useErrorHandling({
    showToast: true,
    logErrors: true,
    retryAttempts: 2,
    retryDelay: 1000,
  });

  // Container-specific success messages
  const showOperationSuccess = useCallback((operation: string) => {
    const messages = {
      create: '托盘创建成功',
      update: '托盘更新成功',
      delete: '托盘删除成功',
      enable: '托盘启用成功',
      disable: '托盘禁用成功',
      batchEnable: '批量启用成功',
      batchDisable: '批量禁用成功',
    };
    
    const message = messages[operation as keyof typeof messages] || `${operation}成功`;
    errorHandling.showSuccess(message);
  }, [errorHandling]);

  // Container-specific batch result handling
  const showBatchResult = useCallback((
    successCount: number,
    failureCount: number,
    operation: string = '操作'
  ) => {
    Toast.showBatchResult(successCount, failureCount, operation);
  }, []);

  // Handle container API responses
  const handleContainerResponse = useCallback(<T>(
    response: { success: boolean; data: T | null; errorMessage?: string | null },
    successMessage?: string
  ): T | null => {
    if (response.success && response.data !== null) {
      if (successMessage) {
        errorHandling.showSuccess(successMessage);
      }
      return response.data;
    } else {
      const error = {
        type: 'business' as const,
        message: response.errorMessage || '操作失败',
        code: 'API_ERROR',
      };
      errorHandling.setError(error);
      return null;
    }
  }, [errorHandling]);

  return {
    ...errorHandling,
    showOperationSuccess,
    showBatchResult,
    handleContainerResponse,
  };
}

/**
 * Form error handling hook
 */
export function useFormErrorHandling() {
  const errorHandling = useErrorHandling({
    showToast: false, // Forms usually show inline errors
    logErrors: true,
  });

  // Form-specific error handling
  const handleFormError = useCallback((error: any, fieldName?: string) => {
    const errorInfo = handleError(error);
    
    // For validation errors, we might want to handle them differently
    if (errorInfo.type === 'validation' && fieldName) {
      // Could integrate with form validation here
      console.warn(`Validation error for field ${fieldName}:`, errorInfo.message);
    }
    
    errorHandling.setError(errorInfo);
    return errorInfo;
  }, [errorHandling]);

  // Handle form submission
  const handleFormSubmission = useCallback(async <T>(
    submitFunction: () => Promise<T>,
    options: {
      onSuccess?: (result: T) => void;
      onError?: (error: ErrorInfo) => void;
      successMessage?: string;
    } = {}
  ): Promise<boolean> => {
    const result = await errorHandling.handleAsyncOperation(submitFunction, {
      ...options,
      showErrorToast: false, // Forms handle errors inline
    });
    
    return result !== null;
  }, [errorHandling]);

  return {
    ...errorHandling,
    handleFormError,
    handleFormSubmission,
  };
}

export default useErrorHandling;